// <predictive-search>: native /search/suggest.json → hot-items focus panel +
// two-panel (collections/suggestions + product cards) typing results.
// App-independent: the `query` resource is used only if returned.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const hl = (text, q) => {
  const t = String(text ?? "");
  if (!q) return esc(t);
  const i = t.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return esc(t);
  return esc(t.slice(0, i)) + '<span class="jg-ps__hl">' + esc(t.slice(i, i + q.length)) + "</span>" + esc(t.slice(i + q.length));
};

customElements.define(
  "predictive-search",
  class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.panel = this.querySelector("[data-ps-panel]");
      this.hot = this.querySelector("[data-ps-hot]");
      this.results = this.querySelector("[data-ps-results]");
      this.loading = this.querySelector("[data-ps-loading]");
      this.clearBtn = this.querySelector("[data-ps-clear]");
      this.searchAll = this.querySelector("[data-ps-search-all]");
      if (!this.input || !this.panel) return;

      this.layout = this.dataset.layout === "list" ? "list" : "cards";
      this.showSidebar = this.dataset.showSidebar !== "false";
      this.productLimit = parseInt(this.dataset.productLimit, 10) || 6;
      this.collectionLimit = parseInt(this.dataset.collectionLimit, 10) || 4;
      this.searchUrl = (window.routes && window.routes.search_url) || "/search";

      this.input.setAttribute("role", "combobox");
      this.input.setAttribute("aria-expanded", "false");
      this.input.setAttribute("aria-autocomplete", "list");
      this.panel.setAttribute("role", "listbox");

      this.input.addEventListener("focus", () => this.open());
      this.input.addEventListener("input", () => this.onInput());
      this.input.addEventListener("keydown", (e) => this.onKey(e));
      if (this.clearBtn) this.clearBtn.addEventListener("click", () => this.onClear());
      this.syncSearchAll();
      document.addEventListener("click", (e) => { if (!this.contains(e.target)) this.close(); });
      this.prefetchHot();
    }

    // Prefetch focus-state suggestions on page load so the dropdown is populated
    // immediately (no loading flash). Seed = first popular term; skips product
    // cards when a server-rendered trending collection is already present.
    async prefetchHot() {
      const target = this.hot && this.hot.querySelector("[data-ps-hot-dynamic]");
      const chip = this.hot && this.hot.querySelector(".jg-ps__chip");
      const seed = chip && chip.textContent.trim();
      if (!target || !seed) return;
      try {
        const base = (window.routes && window.routes.predictive_search_url) || "/search/suggest";
        const limit = Math.min(10, Math.max(this.productLimit, this.collectionLimit));
        const url = `${base}.json?q=${encodeURIComponent(seed)}&resources[type]=product,collection&resources[limit]=${limit}&resources[options][unavailable_products]=last`;
        const data = await (await fetch(url)).json();
        const r = (data.resources && data.resources.results) || {};
        const products = (r.products || []).slice(0, this.productLimit);
        const collections = (r.collections || []).slice(0, this.collectionLimit);
        const hasServerTrending = !!this.hot.querySelector(".jg-ps__grid");
        let html = "";
        if (products.length && !hasServerTrending) {
          html += `<div class="jg-ps__group"><p class="jg-ps__heading">${esc(this.dataset.suggestionsLabel || "Suggestions")}</p><div class="jg-ps__grid jg-ps__grid--cards">${products.map((p) => this.productHtml(p, "")).join("")}</div></div>`;
        }
        if (collections.length) {
          html += `<div class="jg-ps__group"><p class="jg-ps__heading">${esc(this.dataset.collectionsLabel || "Collections")}</p><div class="jg-ps__chips">${collections.map((c) => `<a href="${esc(c.url)}" class="jg-ps__chip">${esc(c.title)}</a>`).join("")}</div></div>`;
        }
        target.innerHTML = html;
        target.classList.add("jg-ps__group");
      } catch {
        /* focus panel still has server-rendered content */
      }
    }

    open() {
      const q = this.input.value.trim();
      if (q.length < 2 && (!this.hot || this.hot.children.length === 0)) return; // nothing to show yet
      this.panel.hidden = false;
      this.input.setAttribute("aria-expanded", "true");
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;
      if (q.length >= 2) this.showResults(); else this.showHot();
    }
    close() {
      this.panel.hidden = true;
      this.input.setAttribute("aria-expanded", "false");
    }
    showHot() { if (this.hot) this.hot.hidden = false; this.results.hidden = true; if (this.loading) this.loading.hidden = true; }
    showResults() { if (this.hot) this.hot.hidden = true; this.results.hidden = false; }

    // In-flight placeholder: 6 shimmering suggestion-card skeletons (jg-ps__* +
    // the global .jg-skel paint), swapped for real results when the fetch lands.
    skeletonHtml() {
      const cell =
        '<div class="jg-ps__card"><span class="jg-ps__card-media jg-skel"></span>' +
        '<span class="jg-skel" style="height:12px;width:80%;border-radius:6px"></span>' +
        '<span class="jg-skel" style="height:12px;width:45%;border-radius:6px"></span></div>';
      const cells = new Array(7).join(cell); // 6 placeholder cards
      return '<div class="jg-ps__grid jg-ps__grid--cards">' + cells + "</div>";
    }

    // The phone sheet's full-width button under the field (snippets/predictive-search.liquid):
    // labelled with the query as typed, hidden while the field is empty.
    syncSearchAll() {
      if (!this.searchAll) return;
      const q = this.input.value.trim();
      this.searchAll.hidden = q.length === 0;
      const label = this.searchAll.querySelector("span");
      if (label) label.textContent = (this.dataset.searchAll || 'Search all "%q%"').replace("%q%", q);
    }

    onClear() {
      if (this.input.value) { this.input.value = ""; if (this.clearBtn) this.clearBtn.hidden = true; this.syncSearchAll(); this.showHot(); this.input.focus(); }
      else { this.close(); this.input.blur(); }
    }

    onInput() {
      clearTimeout(this.t);
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;
      this.syncSearchAll();
      if (q.length < 2) {
        if (this.hot && this.hot.children.length) this.showHot(); else this.close();
        return;
      }
      this.showResults();
      if (this.loading) this.loading.hidden = true;
      this.results.innerHTML = this.skeletonHtml();
      this.t = setTimeout(() => this.fetchResults(q), 180);
    }

    onKey(e) {
      if (e.key === "Escape") { this.close(); this.input.blur(); return; }
      const container = this.results.hidden ? this.hot : this.results;
      if (!container) return;
      const items = Array.from(container.querySelectorAll("a"));
      if (!items.length) return;
      const i = items.indexOf(document.activeElement);
      if (e.key === "ArrowDown") { e.preventDefault(); (items[i + 1] || items[0]).focus(); }
      else if (e.key === "ArrowUp") { e.preventDefault(); (items[i - 1] || items[items.length - 1]).focus(); }
    }

    // Product cards come from the results page's own search (sections/predictive-products.liquid),
    // not from suggest.json: Shopify's predictive endpoint mixes in semantic "related" products
    // -- "ghost" returned DualSense pads and left out Ghost Recon and Ghostrunner, even with
    // fields=title. Suggestions and collections still come from suggest.json, each type with its
    // own limit so query suggestions no longer crowd products out. Both run in parallel.
    async fetchLiteralProducts(q) {
      try {
        const url = `${this.searchUrl}?q=${encodeURIComponent(q)}&type=product&options[prefix]=last&options[unavailable_products]=last&section_id=predictive-products`;
        const res = await fetch(url);
        if (!res.ok) return null;
        const doc = new DOMParser().parseFromString(await res.text(), "text/html");
        const data = doc.querySelector("[data-ps-products]");
        return data ? JSON.parse(data.textContent) : null;
      } catch {
        return null; // fall back to suggest.json's products rather than an empty panel
      }
    }

    async fetchResults(q) {
      try {
        const base = (window.routes && window.routes.predictive_search_url) || "/search/suggest";
        const limit = Math.min(10, Math.max(this.productLimit, this.collectionLimit));
        const url = `${base}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,query,page,article&resources[limit]=${limit}&resources[limit_scope]=each&resources[options][unavailable_products]=last`;
        const [data, literal] = await Promise.all([fetch(url).then((r) => r.json()), this.fetchLiteralProducts(q)]);
        if (this.input.value.trim() !== q) return; // ignore stale responses
        const results = (data.resources && data.resources.results) || {};
        // A typo the literal search cannot place still gets predictive search's best guess.
        if (literal && literal.length) results.products = literal;
        this.render(q, results);
      } catch {
        if (this.loading) this.loading.hidden = true;
      }
    }

    money(v) { return `${this.dataset.currencySymbol || "$"}${v}`; }

    productHtml(p, q) {
      const media = `<span class="jg-ps__card-media">${p.image ? `<img class="jg-ps__card-img" src="${esc(p.image)}&width=400" width="90" height="90" loading="lazy" alt="">` : ""}</span>`;
      const price = p.price != null && p.price !== "" ? `<span class="jg-ps__${this.layout === "cards" ? "card" : "row"}-price">${this.money(p.price)}</span>` : "";
      if (this.layout === "cards") {
        return `<a data-ps-item role="option" href="${esc(p.url)}" class="jg-ps__card">${media}<span class="jg-ps__card-title">${hl(p.title, q)}</span>${price}</a>`;
      }
      return `<a data-ps-item role="option" href="${esc(p.url)}" class="jg-ps__row">${media}<span class="jg-ps__col"><span class="jg-ps__row-title">${hl(p.title, q)}</span>${price}</span></a>`;
    }

    render(q, r) {
      const products = (r.products || []).slice(0, this.productLimit);
      const collections = (r.collections || []).slice(0, this.collectionLimit);
      const queries = (r.queries || []).slice(0, 4);
      const content = (r.pages || []).concat(r.articles || []);

      let main = "";
      if (products.length) {
        main += `<div class="jg-ps__${this.layout === "cards" ? "grid jg-ps__grid--cards" : "list"}">${products.map((p) => this.productHtml(p, q)).join("")}</div>`;
      }
      if (content.length) {
        main += `<div class="jg-ps__list">${content.map((c) => `<a data-ps-item role="option" href="${esc(c.url)}" class="jg-ps__row"><span class="jg-ps__col"><span class="jg-ps__row-title">${hl(c.title, q)}</span></span></a>`).join("")}</div>`;
      }
      // The phone sheet stacks the suggestions UNDER the products, which take the screen first
      // (owner, 2026-10-10), so the markup follows suit: screen readers and the arrow keys walk
      // the DOM, not the picture. It drops the "Search for" row there too -- the pinned
      // "Search all" button goes to the same page, and the row would land between the two.
      // The CSS orders both tiers on its own as well, for a phone turned sideways mid-search.
      const phone = this.dataset.context === "mobile" && !window.matchMedia("(min-width: 640px)").matches;
      const viewAll = (this.dataset.viewAll || 'Search for "%q%"').replace("%q%", esc(q));
      const viewAllRow = phone ? "" : `<a data-ps-item role="option" href="${esc(this.searchUrl)}?q=${encodeURIComponent(q)}&type=product" class="jg-ps__viewall">${viewAll}</a>`;

      let sidebar = "";
      if (this.showSidebar) {
        let s = "";
        if (queries.length) {
          s += `<div class="jg-ps__group"><p class="jg-ps__heading">${esc(this.dataset.suggestionsLabel || "Suggestions")}</p>${queries
            .map((qq) => `<a data-ps-item role="option" href="${esc(qq.url || this.searchUrl + "?q=" + encodeURIComponent(qq.text))}" class="jg-ps__sugg">${hl(qq.text, q)}</a>`)
            .join("")}</div>`;
        }
        if (collections.length) {
          s += `<div class="jg-ps__group"><p class="jg-ps__heading">${esc(this.dataset.collectionsLabel || "Collections")}</p>${collections
            .map((c) => `<a data-ps-item role="option" href="${esc(c.url)}" class="jg-ps__coll">${c.image ? `<img class="jg-ps__coll-img" src="${esc(c.image)}&width=56" width="28" height="28" loading="lazy" alt="">` : ""}<span class="jg-ps__col"><span>${hl(c.title, q)}</span></span></a>`)
            .join("")}</div>`;
        }
        if (s) sidebar = `<div class="jg-ps__sidebar">${s}</div>`;
      }

      if (!products.length && !collections.length && !queries.length && !content.length) {
        this.results.innerHTML = `<div class="jg-ps__empty"><p class="jg-ps__empty-title">${esc(this.dataset.emptyLabel)} "${esc(q)}"</p><p class="jg-ps__empty-hint">${esc(this.dataset.emptyHint)}</p></div>`;
      } else {
        const results = `<div class="jg-ps__main">${main}${viewAllRow}</div>`;
        this.results.innerHTML = `<div class="jg-ps__layout ${sidebar ? "jg-ps__layout--sidebar" : ""}">${phone ? results + sidebar : sidebar + results}</div>`;
      }
      if (this.loading) this.loading.hidden = true;
      this.showResults();
    }
  }
);
